import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Group, Loader, Stack, Text, TextInput, Title } from '@mantine/core';
import { useKiosk } from '@/providers/kioskState';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useEquipmentByTag, useEquipment, formatProblemError } from '@commander/api-client';

export function HomeScreen() {
  const { scanEquipment, routeCheckout, routeCheckin, showInfo } = useKiosk();
  const isOnline = useOnlineStatus();
  const [inputValue, setInputValue] = useState('');
  const [scannedId, setScannedId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Keep input permanently focused so barcode scans always register
  const refocusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    refocusInput();
    // Re-focus whenever the window/tab regains focus
    window.addEventListener('focus', refocusInput);
    return () => window.removeEventListener('focus', refocusInput);
  }, [refocusInput]);

  // Look up equipment when scanned
  const { data: equipment, isFetching, error } = useEquipmentByTag(scannedId ?? '');

  const { data: availableData } = useEquipment({ status: 'available', limit: 50 });
  const { data: unavailableData } = useEquipment({ status: 'defective,maintenance', limit: 50 });
  const availableItems = availableData?.items ?? [];
  const defectiveItems = unavailableData?.items ?? [];
  const MAX_VISIBLE_AVAILABLE = 8;
  const visibleAvailable = availableItems.slice(0, MAX_VISIBLE_AVAILABLE);
  const hiddenAvailableCount = availableItems.length - visibleAvailable.length;

  // Route based on equipment state — wait for fresh data (not stale cache)
  useEffect(() => {
    if (equipment && !isFetching) {
      scanEquipment(equipment.id);
      const status = equipment.status.toLowerCase().replace(/\s+/g, '-');
      const isCheckedOut = !!equipment.currentSessionId;

      if (status === 'defective') {
        showInfo(
          `${equipment.tagId} is marked as defective and cannot be processed.`,
          'warning',
          equipment.description || undefined,
        );
      } else if (status === 'maintenance') {
        showInfo(
          `${equipment.tagId} is currently under maintenance.`,
          'warning',
          equipment.description || undefined,
        );
      } else if (isCheckedOut) {
        routeCheckin();
      } else {
        routeCheckout();
      }
    }
  }, [equipment, isFetching, scanEquipment, routeCheckout, routeCheckin, showInfo]);

  useEffect(() => {
    if (error) {
      showInfo(
        formatProblemError(error, 'Equipment not found'),
        'error',
      );
    }
  }, [error, showInfo]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = inputRef.current?.value?.trim() || inputValue.trim();
    if (!value) return;
    if (!isOnline) {
      setInputValue('');
      showInfo('Kiosk is offline — try again when the connection is restored.', 'warning');
      return;
    }
    setInputValue(value);
    setScannedId(value);
    if (formRef.current) {
      formRef.current.classList.remove('kiosk-scan-flash');
      void formRef.current.offsetWidth;
      formRef.current.classList.add('kiosk-scan-flash');
    }
  };

  return (
    <Stack align="center" gap={20}>
      {/* Hero — Scan icon + heading */}
      <Stack align="center" gap={6}>
        <Box
          className="kiosk-glow kiosk-float"
          style={{
            width: 56,
            height: 56,
            borderRadius: 'var(--radius-lg)',
            background: 'linear-gradient(135deg, var(--cmd-accent) 0%, #2563EB 60%, #1D4ED8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 24px rgba(59, 130, 246, 0.25), 0 2px 8px rgba(0, 0, 0, 0.10)',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <rect x="7" y="7" width="10" height="10" rx="1" />
          </svg>
        </Box>

        <Title
          order={2}
          ta="center"
          fw={700}
          style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--text-primary)',
            fontSize: 'clamp(1.25rem, 4vw, 1.5rem)',
            letterSpacing: '-0.02em',
          }}
        >
          Scan Equipment
        </Title>
        <Text
          ta="center"
          fw={500}
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: '0.8125rem',
          }}
        >
          Scan the radio barcode first, then your ID badge
        </Text>
      </Stack>

      {/* Scan Input */}
      <Box
        ref={formRef}
        component="form"
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 480,
          padding: 'var(--space-5)',
          borderRadius: 'var(--radius-xl)',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border-colour)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <Stack gap="sm">
          <TextInput
            ref={inputRef}
            size="xl"
            placeholder="Scan radio barcode"
            value={inputValue}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            onBlur={refocusInput}
            styles={{
              input: {
                textAlign: 'center',
                fontFamily: 'var(--font-mono)',
                fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                fontWeight: 500,
                height: 64,
                borderWidth: 1,
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'var(--bg-base)',
                borderColor: 'var(--border-colour)',
                color: 'var(--text-primary)',
                boxShadow: 'var(--shadow-inner)',
                letterSpacing: '0.04em',
                caretColor: 'transparent',
              },
            }}
            disabled={isFetching || !isOnline}
            rightSection={isFetching ? <Loader size="sm" color="blue" /> : undefined}
          />

          <Button
            type="submit"
            size="xl"
            h={56}
            fullWidth
            loading={isFetching}
            disabled={!inputValue.trim() || !isOnline}
            styles={{
              root: {
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-lg)',
                fontFamily: 'var(--font-heading)',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                backgroundColor: 'var(--cmd-accent)',
                boxShadow: '0 4px 16px rgba(59, 130, 246, 0.25)',
                border: 'none',
                color: 'white',
              },
            }}
          >
            Look Up
          </Button>
        </Stack>
      </Box>

      {/* Equipment Status Panels — two-column grid */}
      <Box
        style={{
          width: '100%',
          maxWidth: 820,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-4)',
        }}
      >
        {/* Left Column — Unavailable */}
        <Box
          style={{
            borderRadius: 'var(--radius-xl)',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-colour)',
            overflow: 'hidden',
          }}
        >
          {/* Defective / Unavailable Section */}
          <Box style={{ padding: 'var(--space-3) var(--space-5) var(--space-4)' }}>
            <Group justify="space-between" mb={8}>
              <Group gap={8}>
                <Box
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: defectiveItems.length > 0 ? 'var(--amber-accent)' : 'var(--text-muted)',
                  }}
                />
                <Text
                  fw={600}
                  style={{
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--text-muted)',
                    fontSize: '0.6875rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Unavailable
                </Text>
              </Group>
              <Text
                fw={700}
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: defectiveItems.length > 0 ? 'var(--amber-accent)' : 'var(--text-muted)',
                  fontSize: '0.75rem',
                }}
              >
                {defectiveItems.length}
              </Text>
            </Group>

            {defectiveItems.length === 0 ? (
              <Text
                fw={500}
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'var(--text-muted)',
                  fontSize: '0.8125rem',
                }}
              >
                All equipment operational
              </Text>
            ) : (
              <Stack gap={6}>
                {defectiveItems.map((eq) => (
                  <Group
                    key={eq.id}
                    gap={8}
                    wrap="nowrap"
                    style={{
                      padding: '6px 10px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--amber-bg)',
                      border: '1px solid rgba(245, 158, 11, 0.12)',
                    }}
                  >
                    <Text
                      fw={700}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--amber-accent)',
                        fontSize: '0.75rem',
                        letterSpacing: '0.02em',
                        flexShrink: 0,
                      }}
                    >
                      {eq.tagId}
                    </Text>
                    <Box style={{ width: 1, height: 14, background: 'var(--border-colour)', flexShrink: 0 }} />
                    <Text
                      fw={500}
                      style={{
                        fontFamily: 'var(--font-body)',
                        color: 'var(--text-secondary)',
                        fontSize: '0.75rem',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {eq.description}
                    </Text>
                    <Text
                      fw={600}
                      style={{
                        fontFamily: 'var(--font-heading)',
                        color: 'var(--amber-accent)',
                        fontSize: '0.625rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        flexShrink: 0,
                      }}
                    >
                      {eq.status}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Box>
        </Box>

        {/* Right Column — Available Equipment */}
        <Box
          style={{
            borderRadius: 'var(--radius-xl)',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-colour)',
            overflow: 'hidden',
          }}
        >
          <Box style={{ padding: 'var(--space-4) var(--space-5)' }}>
            <Group justify="space-between" mb={8}>
              <Group gap={8}>
                <Box
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: availableItems.length > 0 ? 'var(--green-accent)' : 'var(--text-muted)',
                    boxShadow: availableItems.length > 0 ? '0 0 6px rgba(34, 197, 94, 0.3)' : 'none',
                  }}
                />
                <Text
                  fw={600}
                  style={{
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--text-muted)',
                    fontSize: '0.6875rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Available
                </Text>
              </Group>
              <Text
                fw={700}
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: availableItems.length > 0 ? 'var(--green-accent)' : 'var(--text-muted)',
                  fontSize: '0.75rem',
                }}
              >
                {availableItems.length}
              </Text>
            </Group>

            {availableItems.length === 0 ? (
              <Text
                fw={500}
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'var(--text-muted)',
                  fontSize: '0.8125rem',
                }}
              >
                No equipment available
              </Text>
            ) : (
              <Stack gap={6}>
                {visibleAvailable.map((eq) => (
                  <Group
                    key={eq.id}
                    gap={8}
                    wrap="nowrap"
                    style={{
                      padding: '6px 10px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--green-bg)',
                      border: '1px solid rgba(34, 197, 94, 0.10)',
                    }}
                  >
                    <Text
                      fw={700}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--green-accent)',
                        fontSize: '0.75rem',
                        letterSpacing: '0.02em',
                        flexShrink: 0,
                      }}
                    >
                      {eq.tagId}
                    </Text>
                    <Box style={{ width: 1, height: 14, background: 'var(--border-colour)', flexShrink: 0 }} />
                    <Text
                      fw={500}
                      style={{
                        fontFamily: 'var(--font-body)',
                        color: 'var(--text-secondary)',
                        fontSize: '0.75rem',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {eq.description}
                    </Text>
                  </Group>
                ))}
                {hiddenAvailableCount > 0 && (
                  <Text
                    ta="center"
                    fw={500}
                    style={{
                      fontFamily: 'var(--font-body)',
                      color: 'var(--text-muted)',
                      fontSize: '0.75rem',
                      paddingTop: 2,
                    }}
                  >
                    +{hiddenAvailableCount} more
                  </Text>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}
